import React, { useState, useRef, useEffect } from "react";
import { Add, ArrowUpward } from "@mui/icons-material";
import { ArrowDownward } from "@mui/icons-material";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";
import {
  Search,
  GetApp,
  Print,
  ViewColumn,
  FilterList,
  Clear,
  UpdateRounded,
  Schedule,
  TransferWithinAStation,
} from "@mui/icons-material";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  useCreateAccountMutation,
  useCreateAppointmentMutation,
  useCreateUserCaseInviteMutation,
  useCreateUserCaseMutation,
  useGetPartnersQuery,
  useGetVideoCallTokenQuery,
} from "state/api";
import { useTheme } from "@mui/material/styles";
import { tokensDark, tokensLight } from "theme";
import {
  Assignment,
  Dashboard,
  Edit,
  Share,
  Update,
} from "@mui/icons-material";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import parsePhoneNumberFromString from "libphonenumber-js";
const PatientTable = ({ patients, handleAddPatient }) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const tableRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const CountryNameByPhoneCode = ({ phoneNumber }) => {
    const country = parsePhoneNumberFromString(phoneNumber, "IN");
    const countryName = country ? country.country : "Country not found";

    return countryName;
  };
  const [filterCriteria, setFilterCriteria] = useState({
    name: "",
    email: "",
    mobile: "",
    country: "",
    role: "",
  });
  const [filteredPatients, setFilteredPatients] = useState(patients); // New state for filtered results
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(""); // New state for search query
  const [sortOrder, setSortOrder] = useState("asc");
  const handleRowClick = (patient) => {
    if (selectedPatient === patient) {
      setSelectedPatient(null);
    } else {
      setSelectedPatient(patient);
    }
  };

  const handleFilterDialogOpen = () => {
    setOpenFilterDialog(true);
  };

  const handleFilterDialogClose = () => {
    setOpenFilterDialog(false);
  };

  const handleSortOrderChange = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);
  };
  const sortTableData = () => {
    const sortedData = [...patients].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    setFilteredPatients(sortedData);
  };

  useEffect(() => {
    sortTableData();
  }, [sortOrder]);

  const handleFilterCriteriaChange = (event, field) => {
    const { value } = event.target;
    setFilterCriteria((prevFilterCriteria) => ({
      ...prevFilterCriteria,
      [field]: value,
    }));

    const filteredData = patients.filter((patient) => {
      const { name, email, mobile, country, role } = patient;
      const {
        name: nameFilter,
        email: emailFilter,
        mobile: mobileFilter,
        country: countryFilter,
        role: roleFilter,
      } = filterCriteria;
    
      const isNameMatch =
        !nameFilter || name.toLowerCase().includes(nameFilter.toLowerCase());
      const isEmailMatch =
        !emailFilter || email.toLowerCase().includes(emailFilter.trim().toLowerCase());
      const isMobileMatch = !mobileFilter || mobile.includes(mobileFilter);
      const isCountryMatch =
        !countryFilter ||
        country.toLowerCase().includes(countryFilter.toLowerCase());
      const isRoleMatch =
        !roleFilter || role.toLowerCase().includes(roleFilter.toLowerCase());
    
      return (
        isNameMatch &&
        isEmailMatch &&
        isMobileMatch &&
        isCountryMatch &&
        isRoleMatch
      );
    });
    console.log("the data filtered is", filteredData)
    console.log("the patient data is", patients)
    setFilteredPatients(filteredData);
    setCurrentPage(1);    
  };

  const handleResetFilterCriteria = () => {
    setFilterCriteria({
      name: "",
      email: "",
      mobile: "",
      country: "",
      role: "",
    });
    setFilteredPatients(patients); // Reset filtered results to original data
    setCurrentPage(1);
  };

  const handlePrint = () => {
    const tableContent = tableRef.current.cloneNode(true);
    const buttons = tableContent.querySelectorAll('button');
    buttons.forEach(button => button.remove());
    const content = tableContent.innerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print</title>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownload = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," + convertToCSV(filteredPatients);

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "patients.csv");
    document.body.appendChild(link); // Required for Firefox
    link.click();
  };

  const convertToCSV = (data) => {
    const header = ["Name", "Email", "Phone Number", "Country", "Role"];
    const rows = data.map((patient) => [
      patient.name,
      patient.email,
      `"${patient.mobile}"`, // Treat phone number as a string
      patient.country ? patient.country : "India",
      patient.role,
    ]);

    const csvData = [header, ...rows]
      .map((row) => row.map((value) => `"${value}"`).join(","))
      .join("\n");
    return csvData;
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      setFilteredPatients(patients); // Restore the original table if search query is empty
    } else {
      const searchedData = filteredPatients.filter((patient) =>
        Object.values(patient)
          .join(" ")
          .toLowerCase()
          .includes(query.toLowerCase())
      );

      setFilteredPatients(searchedData);
      setCurrentPage(1);
    }
  };

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPatients.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderRow = (patient, idx) => {
    const { name, country, mobile, email, role } = patient;
    const backgroundColor = theme.palette.primary.light;

    return (
      <TableRow
        className='cursor-pointer'
        sx={{
          backgroundColor: backgroundColor,
          color: isDarkMode
            ? tokensDark.secondary[800]
            : tokensDark.secondary[700],
          ":hover": {
            backgroundColor: isDarkMode
              ? tokensDark.primary[300]
              : tokensDark.grey[200],
          },
        }}
        key={patient._id}
        onClick={() => handleRowClick(patient)}>
        <TableCell>{name}</TableCell>
        <TableCell>{email ? email : 'Email Not Provided'}</TableCell>
        <TableCell>{mobile}</TableCell>
        <TableCell>
          {mobile ? CountryNameByPhoneCode({ phoneNumber: mobile }) : ""}
        </TableCell>
        {/* <TableCell>{role}</TableCell> */}
      </TableRow>
    );
  };

  const HandleDashboard = (patient) => {
    navigate(`/patient?patient=${patient._id}&mobile=${patient.mobile}`);
  };

  const HandleAssignPlan = (patient) => {
    navigate(
      `/patient/assign-plan?patient=${patient._id}&mobile=${patient.mobile}`
    );
  };
  const [userData] = useOutletContext();
  const [showSchedule, setShowSchedule] = useState(false);
  const [showSelectPartner, setShowSelectPartner] = useState(false);
  const renderExpandedRow = (patient, idx, userData) => {
    const backgroundColor = theme.palette.background.default;

    return (
      <TableRow
        sx={{
          cursor: "pointer",
          backgroundColor: backgroundColor,
          color: isDarkMode
            ? tokensDark.secondary[800]
            : tokensDark.primary[700],
        }}
        key={`${patient.id}-expanded`}>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, cursor: "pointer" }}
          colSpan={5}>
          <div className='expanded-row'>
            <Button
              startIcon={<Dashboard />}
              variant='outlined'
              style={{
                margin: "8px",
                color: isDarkMode ? "white" : "black",
                borderColor: isDarkMode ? "white" : "black",
              }}
              onClick={() => {
                HandleDashboard(patient);
              }}>
              View Dashboard
            </Button>
            {!userData.team && userData.userData[0].role !== "Client" && (
              <Button
                startIcon={<Assignment />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  HandleAssignPlan(patient);
                }}>
                Assign Plan
              </Button>
            )}
            {userData.team && userData.team.role !== "viewer" && (
              <Button
                startIcon={<Assignment />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  HandleAssignPlan(patient);
                }}>
                Assign Plan
              </Button>
            )}

            {!userData.team && userData.userData[0].role !== "Client" && (
              <Button
                startIcon={<Edit />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  navigate(
                    `/patient/profile?patient=${patient._id}&mobile=${patient.mobile}&name=${patient.name}`
                  );
                }}>
                Edit Patient Profile
              </Button>
            )}
            {userData.team && userData.team.role !== "viewer" && (
              <Button
                startIcon={<Edit />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  navigate(
                    `/patient/profile?patient=${patient._id}&mobile=${patient.mobile}&name=${patient.name}`
                  );
                }}>
                Edit Patient Profile
              </Button>
            )}

            {!userData.team && userData.userData[0].role !== "Client" && (
              <Button
                startIcon={<Update />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  navigate(
                    `/patient/update-stats?patient=${patient._id}&mobile=${
                      patient.mobile
                    }?&name=${patient.name}?&plan=${null}`,
                    {
                      state: {
                        case: 1,
                        session: 1,
                        painPoints: [],
                        isPlanSelectionDisable: false,
                      },
                    }
                  );
                }}>
                Add Session Records
              </Button>
            )}
            {userData.team && userData.team.role !== "viewer" && (
              <Button
                startIcon={<Update />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  navigate(
                    `/patient/update-stats?patient=${patient._id}&mobile=${patient.mobile}&name=${patient.name}`
                  );
                }}>
                Add Session Records
              </Button>
            )}
            {!userData.team && userData.userData[0].role !== "Client" && (
              <Button
                startIcon={<Schedule />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  setShowSchedule(true);
                }}>
                Schedule Appointment
              </Button>
            )}
            {userData.team && userData.team.role !== "viewer" && (
              <Button
                startIcon={<Schedule />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  setShowSchedule(true);
                }}>
                Schedule Appointment
              </Button>
            )}
            {!userData.team &&
              userData.userData[0].role !== "Client" &&
              !patient.isCaseTransfered && (
                <Button
                  startIcon={<TransferWithinAStation />}
                  variant='outlined'
                  style={{
                    margin: "8px",
                    color: isDarkMode ? "white" : "black",
                    borderColor: isDarkMode ? "white" : "black",
                  }}
                  onClick={() => {
                    setShowSelectPartner(true);
                  }}>
                  Transfer Case
                </Button>
              )}
            {userData.team && userData.team.role !== "viewer" && (
              <Button
                startIcon={<TransferWithinAStation />}
                variant='outlined'
                style={{
                  margin: "8px",
                  color: isDarkMode ? "white" : "black",
                  borderColor: isDarkMode ? "white" : "black",
                }}
                onClick={() => {
                  setShowSelectPartner(true);
                }}>
                Transfer Case
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };
  const SelectPartner = () => {
    const [selectedPartner, setSelectedPartnerDetail] = useState();
    const [editAccess, setEditAccess] = useState(true);
    const [fullAccess, setFullAccess] = useState(true);
    const [createTransferOwnership] = useCreateUserCaseMutation();
    const [createInviteTransfer] = useCreateUserCaseInviteMutation();
    const [createAccount] = useCreateAccountMutation();
    const [role, setRole] = useState("Doctor");
    const [name, setName] = useState("");
    const [mobile, setMobile] = useState("");
    const [isInvite, setIsInvite] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const { data } = useGetPartnersQuery();
    const handlePartnerSearch = (e) => {
      const ptr = data.filter((option) =>
        option.account
          ? option.account.name
              .trim()
              .toLowerCase()
              .includes(e.target.value.trim().toLowerCase())
          : option.name
              .trim()
              .toLowerCase()
              .includes(e.target.value.trim().toLowerCase())
      );

      setSelectedPartnerDetail(ptr[0]);
      setSearchTerm(e.target.value);
    };
    const handleCreateUserCase = () => {
      if (isInvite) {
        const account = { name: name, mobile: mobile, role: role };
        createAccount(account)
          .unwrap()
          .then((d) => {
            const link = "https://casamed.page.link/app";
            const userCase = {
              partnerRecords: [d._id],
              primaryPartner: userData.userData[0]._id,
              secondaryPartner: d._id,
              currentPartner: d._id,
              user: selectedPatient._id,
              secondaryFullAccess: fullAccess,
              secondaryEditAccess: editAccess,
              currentEditAccess: editAccess,
              currentFullAccess: fullAccess,
            };
            createInviteTransfer({
              userCase: userCase,
              account: account,
              name: userData.userData[0].name,
              link: link,
            })
              .unwrap()
              .then((d) => {
                alert("Transferred Case Successfully!");
                setShowSelectPartner(false);
                window.location.reload();
              })
              .catch((e) => {
                alert("Something went wrong!");
              });
          })
          .catch((e) => {
            alert("Something went wrong!");
          });
      } else {
        const userCase = {
          partnerRecords: [
            selectedPartner.account
              ? selectedPartner.account._id
              : selectedPartner.user._id,
          ],
          primaryPartner: userData.userData[0]._id,
          secondaryPartner: selectedPartner.account
            ? selectedPartner.account._id
            : selectedPartner.user._id,
          currentPartner: selectedPartner.account
            ? selectedPartner.account._id
            : selectedPartner.user._id,
          user: selectedPatient._id,
          secondaryFullAccess: fullAccess,
          secondaryEditAccess: editAccess,
          currentEditAccess: editAccess,
          currentFullAccess: fullAccess,
        };
        createTransferOwnership(userCase)
          .unwrap()
          .then((d) => {
            alert("Transferred Case Successfully!");
            setShowSelectPartner(false);
            window.location.reload();
          })
          .catch((e) => {
            alert("Something went wrong while transferring the case");
          });
      }
    };

    return (
      <div className='fixed  inset-0 z-50 flex items-center mt-16 md:mt-0 justify-center bg-black bg-opacity-50'>
        <div className='bg-white overflow-y-auto h-full overflow-auto  rounded-t-lg md:rounded-lg p-6'>
          <h2 className='text-lg text-gray-700 font-semibold mb-4'>
            Select the Partner to Transfer
          </h2>
          <input
            type='text'
            placeholder='Search'
            className='w-full border rounded-lg px-3 py-2 text-gray-700 mt-2 mb-2'
            onChange={handlePartnerSearch}
          />
          <select
            defaultValue='choose'
            onChange={(e) => {
              // console.log(JSON.parse(e.target.value))
              setSelectedPartnerDetail(JSON.parse(e.target.value));
            }}
            className='w-full border rounded-lg px-3 py-2 text-gray-700'>
            {data &&
              data
                .filter((option) =>
                  option.account
                    ? option.account.name
                        .trim()
                        .toLowerCase()
                        .includes(searchTerm.trim().toLowerCase())
                    : option.name
                        .trim()
                        .toLowerCase()
                        .includes(searchTerm.trim().toLowerCase())
                )
                .map((option) => (
                  <option key={option._id} value={JSON.stringify(option)}>
                    {option.account
                      ? option.account.name
                      : option.user
                      ? option.user.name
                      : option.name}
                  </option>
                ))}
          </select>
          {selectedPartner && (
            <div className='mt-2'>
              {(selectedPartner.account || selectedPartner.user) && (
                <div>
                  {" "}
                  <h2 className='text-sm text-gray-500 '>Role</h2>
                  <h2 className='text-sm text-gray-700 font-semibold mb-4'>
                    {selectedPartner.account
                      ? selectedPartner.account.role
                      : selectedPartner.user.role}
                  </h2>
                </div>
              )}
              <h2 className='text-sm text-gray-500 '>Degrees</h2>
              <h2 className='text-sm text-gray-700 font-semibold mb-4'>
                {selectedPartner.degree && selectedPartner.degree.join(", ")}
              </h2>
              <h2 className='text-sm text-gray-500  '>Specialisations</h2>
              <h2 className='text-sm text-gray-700 font-semibold mb-4'>
                {selectedPartner.specialization &&
                  selectedPartner.specialization.join(", ")}
              </h2>
              <h2 className='text-sm text-gray-500 '>Expertise</h2>
              <h2 className='text-sm text-gray-700 font-semibold mb-4'>
                {selectedPartner.areaOfExpertise &&
                  selectedPartner.areaOfExpertise.join(", ")}
              </h2>
            </div>
          )}
          <h2 className='text-sm text-gray-500 mt-2'>Has Edit Access?</h2>
          <select
            className='w-full text-gray-700 border rounded-lg px-3 py-2'
            onChange={(e) => setEditAccess(e.target.value)}>
            <option value={true}>Yes</option>
            <option value={false}>No</option>
          </select>
          <h2 className='text-sm text-gray-500 mt-2'>Has Full Access?</h2>
          <select
            className='w-full text-gray-700 border rounded-lg px-3 py-2'
            onChange={(e) => setFullAccess(e.target.value)}>
            <option value={true}>Yes</option>
            <option value={false}>No</option>
          </select>

          <h2 className='text-md text-gray-700 font-semibold mt-4 text-center'>
            OR
          </h2>
          <h2
            className='text-sm text-gray-700 font-semibold mt-4 underline cursor-pointer text-center'
            onClick={() => {
              setIsInvite(!isInvite);
            }}>
            {!isInvite ? "Invite Partner" : "Cancel Invitation"}
          </h2>
          {isInvite && (
            <div>
              <input
                type='text'
                placeholder='Enter partner name'
                onChange={(e) => {
                  setName(e.target.value);
                }}
                className='border rounded-lg px-3 py-2 mb-4 text-gray-700 w-full'
              />

              <input
                type='tel'
                placeholder='eg: +919876543210'
                onChange={(e) => {
                  setMobile(e.target.value);
                }}
                className='border rounded-lg px-3 py-2 mb-4 text-gray-700 w-full'
              />
              <h2 className='text-sm text-gray-500 mt-2'>Role</h2>
              <select
                className='w-full text-gray-700 border rounded-lg px-3 py-2'
                onChange={(e) => {
                  setRole(e.target.value);
                }}>
                <option value={"Doctor"}>Doctor</option>
                <option value={"Physio"}>Physio</option>
              </select>
              <h2 className='text-sm text-gray-500 mt-2'>Has Edit Access?</h2>
              <select
                className='w-full border rounded-lg px-3 py-2 text-gray-700'
                onChange={(e) => setEditAccess(e.target.value)}>
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </select>
              <h2 className='text-sm text-gray-500 mt-2'>Has Full Access?</h2>
              <select
                className='w-full border rounded-lg px-3 py-2  text-gray-700'
                onChange={(e) => setFullAccess(e.target.value)}>
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </select>
            </div>
          )}

          <div className='flex gap-2 justify-end mt-4'>
            <button
              onClick={() => {
                setShowSelectPartner(false);
              }}
              className='bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2'>
              Cancel
            </button>
            <button
              onClick={() => {
                handleCreateUserCase();
              }}
              className='bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-4 py-2'>
              Transfer
            </button>
          </div>
        </div>
      </div>
    );
  };
  const DateTimePickerModal = ({ patient }) => {
    const [selectedDateTime, setSelectedDateTime] = useState("");
    const [appointmentType, setAppointmentType] = useState("Physio Evaluation");
    const [department, setDepartment] = useState("orthopedic");
    const [bookAppointment] = useCreateAppointmentMutation();
    const handleDateTimeChange = (e) => {
      console.log(moment(e.target.value).format("YYYY-MM-DDTHH:mm:ss"));
      setSelectedDateTime(e.target.value);
    };

    const handleSave = (patient) => {
      if (selectedDateTime.trim().length === 0) {
        alert("Please enter the date and time for the appointment");
      } else {
        const appointment = {
          user: patient._id,
          partner: !userData.team
            ? userData.userData[0]._id
            : userData.team.partner,
          schedule: moment(selectedDateTime).utc(true),
          appointmentType: appointmentType,
          department: department,
        };
        bookAppointment(appointment)
          .unwrap()
          .then((d) => {
            alert(d.message);
          })
          .catch((e) => {
            console.log(e);
            alert(e.data.message);
          });
      }
    };

    return (
      <div className='fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-50'>
        <div className='bg-white rounded-lg p-6'>
          <h2 className='text-lg text-gray-700 font-semibold mb-4'>
            Select a date and time
          </h2>
          <input
            type='datetime-local'
            value={selectedDateTime}
            onChange={handleDateTimeChange}
            className='border rounded-lg px-3 py-2 mb-4 text-gray-700'
          />
          <h2 className='text-md text-gray-700 font-semibold mt-4'>
            Choose Appointment For
          </h2>
          <select
            onChange={(e) => {
              setAppointmentType(e.target.value);
            }}
            className='w-full text-gray-700'>
            <option value={"Physio Evaluation"}>Physio Evaluation</option>
            <option value={"Physio Session"}>Physio Session</option>
            <option value={"Doctor Consultation"}>Doctor Consultation</option>
            {/* <option>Diet</option>
            <option>Dietician</option> */}
            <option value={"Home Visit"}>Home Visit</option>
          </select>
          <h2 className='text-md text-gray-700 font-semibold mt-4'>
            Choose Department
          </h2>
          <select
            onChange={(e) => {
              setDepartment(e.target.value);
            }}
            className='w-full text-gray-700'>
            <option value={"orthopedic"}>Orthopedic</option>
            <option value={"sports_injury"}>Sports</option>
            <option value={"gynaecology"}>Gynae</option>
            {/* <option>Diet</option>
            <option>Dietician</option> */}
            <option value={"pcod"}>PCOD</option>
          </select>

          <div className='flex gap-2 justify-end mt-4'>
            <button
              onClick={() => {
                setShowSchedule(false);
              }}
              className='bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2'>
              Cancel
            </button>
            <button
              onClick={() => {
                handleSave(patient);
              }}
              className='bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-4 py-2'>
              Schedule
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className='mb-4'>
        {showSchedule && <DateTimePickerModal patient={selectedPatient} />}
        {showSelectPartner && <SelectPartner />}
        <ToastContainer
          containerId='toast'
          autoClose={2000}
          position='top-center'
          hideProgressBar={true}
        />
        <div className='w-full flex justify-between items-center'>
          <TextField
            label='Search by name, email, phone number'
            variant='outlined'
            size='small'
            value={searchQuery}
            onChange={handleSearch}
            className='w-1/2'
          />
          <ButtonGroup>
            <IconButton onClick={handleDownload}>
              <GetApp />
            </IconButton>
            <IconButton onClick={handlePrint}>
              <Print />
            </IconButton>
            <IconButton>
              <ViewColumn />
            </IconButton>
            <IconButton onClick={handleFilterDialogOpen}>
              <FilterList />
            </IconButton>
            {!userData.team && userData.userData[0].role !== "Client" && (
              <IconButton onClick={handleAddPatient}>
                <Add />
              </IconButton>
            )}
            {userData.team && userData.team.role !== "viewer" && (
              <IconButton onClick={handleAddPatient}>
                <Add />
              </IconButton>
            )}
          </ButtonGroup>
        </div>
      </div>
      <TableContainer
        ref={tableRef}
        style={{ marginTop: "16px" }}
        component={Paper}
        className=' shadow-none'>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: isDarkMode
                  ? tokensDark.secondary[800]
                  : tokensDark.secondary[700],
                fontWeight: "bold",
              }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Country</TableCell>
              {/* <TableCell onClick={handleSortOrderChange}>
                Role {sortOrder === "asc" ? <ArrowDownward /> : <ArrowUpward />}{" "}
              </TableCell> */}
            </TableRow>
          </TableHead>

          <TableBody>
            {currentItems.map((patient, idx) => (
              <>
                {!patient.isCaseTransfered && renderRow(patient, idx)}
                {selectedPatient === patient &&
                  renderExpandedRow(patient, idx, userData)}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <div className='justify-center mt-4 w-full overflow-x-auto'>
        <ButtonGroup
          style={{ color: "#755a0e" }}
          className='flex items-center justify-center w-full'>
          <Button
            style={{ color: isDarkMode ? "white" : "#755a0e", border: "solid" }}
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}>
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i + 1}
              style={{
                color: isDarkMode ? "white" : "#755a0e",
                border: "solid",
              }}
              variant={currentPage === i + 1 ? "contained" : "outlined"}
              onClick={() => handlePageChange(i + 1)}>
              {i + 1}
            </Button>
          ))}
          <Button
            style={{ color: isDarkMode ? "white" : "#755a0e", border: "solid" }}
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}>
            Next
          </Button>
        </ButtonGroup>
      </div>

      <Dialog open={openFilterDialog} onClose={handleFilterDialogClose}>
        <DialogTitle>Filter Table Data</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Name'
            type='text'
            fullWidth
            variant='outlined'
            value={filterCriteria.name}
            onChange={(e) => handleFilterCriteriaChange(e, "name")}
          />
          <TextField
            margin='dense'
            label='Email'
            type='email'
            fullWidth
            variant='outlined'
            value={filterCriteria.email}
            onChange={(e) => handleFilterCriteriaChange(e, "email")}
          />
          <TextField
            margin='dense'
            label='Phone Number'
            type='tel'
            fullWidth
            variant='outlined'
            value={filterCriteria.mobile}
            onChange={(e) => handleFilterCriteriaChange(e, "mobile")}
          />
          <TextField
            margin='dense'
            label='Country'
            type='text'
            fullWidth
            variant='outlined'
            value={filterCriteria.country}
            onChange={(e) => handleFilterCriteriaChange(e, "country")}
          />
          <TextField
            margin='dense'
            label='Role'
            type='text'
            fullWidth
            variant='outlined'
            value={filterCriteria.role}
            onChange={(e) => handleFilterCriteriaChange(e, "role")}
          />

          <Button variant='contained' onClick={handleFilterDialogClose}>
            Apply Filter
          </Button>

          <Button
            variant='contained'
            style={{ marginLeft: "8px" }}
            onClick={handleResetFilterCriteria}>
            Reset Filters
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientTable;
